
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, Share2, Facebook, Twitter, Mail, Link as LinkIcon, Ticket, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

interface StreamShareDialogProps {
  streamId: string;
  streamTitle: string;
  inviteRequired?: boolean;
  children: React.ReactNode;
}

export function StreamShareDialog({ streamId, streamTitle, inviteRequired = false, children }: StreamShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const streamUrl = `${window.location.origin}/streams/${streamId}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(streamUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const generateInviteLink = async () => {
    try {
      setGeneratingInvite(true);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 7);
      const response = await api.post('api/invites', {
        streamId: parseInt(streamId, 10),
        maxUses: 1,
        expiresAt: tomorrow.toISOString(),
      });
      const data = response.data as { invite?: { code?: string } } | undefined;
      if (response.success && data?.invite?.code) {
        const code = data.invite.code;
        setInviteCode(code);
      } else {
        throw new Error(response.error?.message || 'Failed to generate invite');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate invite link');
    } finally {
      setGeneratingInvite(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteCode) return;
    const inviteUrl = `${window.location.origin}/streams/${streamId}?invite=${inviteCode}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      toast.error('Failed to copy invite link');
    }
  };

  const shareOptions = [
    {
      name: 'Copy Link',
      icon: copied ? Check : Copy,
      action: copyToClipboard,
      color: 'text-blue-600',
      description: 'Copy link to share anywhere'
    },
    {
      name: 'Email',
      icon: Mail,
      action: () => window.open(`mailto:?subject=${encodeURIComponent(`Check out this stream: ${streamTitle}`)}&body=${encodeURIComponent(`I thought you might be interested in this stream: ${streamUrl}`)}`),
      color: 'text-red-600',
      description: 'Share via email'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(streamUrl)}&quote=${encodeURIComponent(`Check out this stream: ${streamTitle}`)}`),
      color: 'text-blue-700',
      description: 'Share on Facebook'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      action: () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(streamUrl)}&text=${encodeURIComponent(`Check out this stream: ${streamTitle}`)}`),
      color: 'text-blue-500',
      description: 'Share on Twitter'
    }
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Stream
          </DialogTitle>
          <DialogDescription>
            Share "{streamTitle}" with others
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="link" className="w-full">
          <TabsList className={`grid w-full ${inviteRequired ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="link">Link</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            {inviteRequired && <TabsTrigger value="invite">Invite</TabsTrigger>}
          </TabsList>

          <TabsContent value="link" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stream-url">Stream Link</Label>
              <div className="flex gap-2">
                <Input
                  id="stream-url"
                  value={streamUrl}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  className="shrink-0"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can view the stream
              </p>
            </div>
          </TabsContent>

          <TabsContent value="social" className="space-y-4">
            <div className="space-y-3">
              <Label>Share via</Label>
              <div className="grid gap-2">
                {shareOptions.slice(1).map((option) => (
                  <Button
                    key={option.name}
                    variant="outline"
                    className="justify-start h-auto p-3"
                    onClick={option.action}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <option.icon className={`h-5 w-5 ${option.color}`} />
                      <div className="text-left">
                        <div className="font-medium">{option.name}</div>
                        <div className="text-xs text-muted-foreground">{option.description}</div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </TabsContent>

          {inviteRequired && (
            <TabsContent value="invite" className="space-y-4">
              <div className="space-y-2">
                <Label>Generate an invite link</Label>
                <p className="text-xs text-muted-foreground">
                  This stream requires an invite code. Generate a single-use link to share with
                  someone you want to grant access.
                </p>
                {inviteCode ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={`${window.location.origin}/streams/${streamId}?invite=${inviteCode}`}
                        readOnly
                        className="flex-1 font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyInviteLink}
                        className="shrink-0"
                      >
                        {inviteCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Code: <span className="font-mono font-semibold">{inviteCode}</span> — valid for 7 days, single use.
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => setInviteCode(null)}>
                      Generate another
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={generateInviteLink}
                    disabled={generatingInvite}
                    className="w-full"
                  >
                    {generatingInvite ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Ticket className="h-4 w-4 mr-2" />
                        Generate Invite Link
                      </>
                    )}
                  </Button>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>

        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
          <LinkIcon className="h-4 w-4 text-muted-foreground" />
          <div className="text-sm">
            <div className="font-medium">Direct Link</div>
            <div className="text-xs text-muted-foreground">
              {streamUrl}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={copyToClipboard}
            className="ml-auto"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}